import { useState, useEffect, useLayoutEffect, useRef, forwardRef } from 'react';

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { Flex, FlexLine, BasicFlex } from '../../utils/Common';

import {  getStartEndTimes, findEventInDate } from './helpers'

import { HebcalTimedEvent, ScheduledEventMonthView, BasicRibbon, dayNames, FullDateEvent } from './Common';

import { formatTime, getHebrewDate, getEvents, getParsha, getCandleLighting, getHavdala, getDayBefore, getHolidayEvents, dateStart, checkBookingAvailabilityByDate } from '../../utils/util'

import { flags } from '@hebcal/core';

import CalendarEditCur from '../../images/calendar2.png';

import useDateEvents from '../../hooks/timeline/useDateEvents';

import { useCalendarContext } from '../../contexts/Calendar';

import { useNavigate } from 'react-router-dom';

import useQueryParams from '../../hooks/useQueryParams';

import useLocalStylistsStore from '../../store/stylists/local';

const Container = styled.div`

`

const Ribbon = styled(BasicRibbon)`
  height: 6rem;
`

const MainCalendar = styled.div`
  /* min-height: 75rem; */
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  display: grid;
  grid-template-columns: repeat(7, minmax(6rem, 1fr));
  /* grid-template-rows: repeat(5, 12rem); */
  /* grid-auto-rows: minmax(16rem, 1fr); */
  /* gap: 1px; */
  border-left: 1px solid ${props => props.theme.palette.grey[300]};
  /* position: relative; */
`

const DayName = styled.div`
  font-size: 1.8rem;
  font-weight: 500;
  text-transform: uppercase;
`


// STYLES FOR DAY COMPONENT
const DayStyles = styled.div`
  background-color: ${props => props.$today ? props.theme.colors.primaryFaint : props.theme.colors.white};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  /* padding: 1rem; */
  border-right: 1px solid ${props => props.theme.palette.grey[300]};
  border-bottom: 1px solid ${props => props.theme.palette.grey[300]};
  height: 10vw;
  position: relative;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.secondaryFaint};
  }

  ${props => props.$disabled && css/* closed */`
    background: repeating-linear-gradient(
      45deg,
      #f9f0f0aa,
      #f9f0f0aa 4px,
      #e6e2e2aa 4px,
      #e6e2e2aa 8px
    );
    cursor: not-allowed;
  `}

  ${props => props.$warning && css/* closed */`
    background: repeating-linear-gradient(
      45deg,
      #ece0abcc,
      #ece0abcc 4px,
      #e6e2e2aa 4px,
      #e6e2e2aa 8px
    );
    cursor: help;
  `}
`

const MonthDayLine = styled(FlexLine)`
  padding: 1rem;
  cursor: zoom-in;
`

const MonthDay = styled.span`
  color: ${props => props.$active ? props.theme.colors.text : props.theme.colors.disabledText};
  font-weight: 400;
`

const HebrewRegular = styled.span`
  font-size: 1.2rem;
  font-weight: 400;
  font-family: "Bona Nova";
`;

const HebrewMonthDay = styled(HebrewRegular)`
  font-weight: 400;
  font-family: "Bona Nova";
  font-size: 1.2rem;
`

const CandleLighting = styled(HebrewRegular)`
  font-size: 1.2rem;
  font-weight: 400;
  font-family: "Bona Nova";
  color: ${props => props.theme.colors.lightText};
`

const ShabbosDiv = styled.div`
  /* position: absolute;
  bottom: .4rem;
  right: .4rem; */
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: calc(100%);
  overflow: hidden;
  padding: 0 .3rem;
  /* height: 3rem; */
`

const Parsha = styled.span`
  color: ${props => props.theme.colors.lightText};
  font-weight: bold;
  font-family: "Bona Nova";
  letter-spacing: 1px;
`

const ScheduledEvents = styled.div`
  flex-grow: 1;
  width: 100%;
`;

const CalendarEvents = styled.div`
  /* flex-grow: 1; */
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  /* opacity: .6; */
`;

const CalendarEvent = styled(FullDateEvent)`
  width: fit-content;
  padding: 0 .3rem;
  border-radius: .2rem;
  box-shadow: none;
  font-size: 1.2rem !important;
  margin: 0;
  margin-top: .1rem;
`;

const MonthEvent = styled.div`
  width: 100%;
  /* height: 5rem; */
  background-color: lightcyan;
`;

const SomeMore = styled.div`
  width: 100%;
  color: ${props => props.theme.colors.lightText};
  padding-left: .3rem;
  font-size: 1.2rem;
  font-weight: 500;
  cursor: zoom-in;
`;


const Day = ({day, date, fullDate, hebDate, parsha, shabbos, havdalah, candles, candle, calendarEvents, $active, $today, scheduledEvents = [], createEvent}) => {

  const { setting, calOptions } = useCalendarContext() || {};

  let events = getEvents(calOptions, fullDate, fullDate);

  let dayBefore = getDayBefore(fullDate);
  let dbEvents = getEvents(calOptions, dayBefore, dayBefore);

  let parsh = getParsha(events);
  let candleLighting = getCandleLighting(dbEvents);
  let havdala = getHavdala(events);

  let calEvents = getHolidayEvents(fullDate);

  let hebrewDate = getHebrewDate(fullDate);

  const EventRef = useRef(null)

  const [displayedEvents, setDisplayedEvents] = useState({
    moreEvents: 0,
    eventsToDisplay: 0
  })

  useLayoutEffect(() => {
    let height = EventRef?.current?.clientHeight
    let numOfEvents = scheduledEvents.length
    let eventsPerHeight = Math.floor(height / 22)
    let eventsToShow = eventsPerHeight > numOfEvents ? numOfEvents : (eventsPerHeight - 1)
    setDisplayedEvents({
      moreEvents: numOfEvents - eventsToShow,
      eventsToDisplay: eventsToShow
    })
  }, [scheduledEvents])

  let availability = checkBookingAvailabilityByDate(fullDate, setting?.data);

  return (
    <DayStyles $today={$today} onClick={() => {
      if (availability.$disabled) return
      createEvent(fullDate)
    }} {...availability}>
      <MonthDayLine dir="row">
        <MonthDay $active={$active}>{day}</MonthDay>
        <HebrewMonthDay>{hebrewDate}</HebrewMonthDay>
      </MonthDayLine>

      <ScheduledEvents ref={EventRef}>
        {scheduledEvents.slice(0, displayedEvents.eventsToDisplay).map((event, i) => {
          return (
            <ScheduledEventMonthView key={i} event={event} />
          )
        })
        }
        {displayedEvents.moreEvents ? <SomeMore>{displayedEvents.moreEvents}+ More...</SomeMore> : null}
      </ScheduledEvents>

      <CalendarEvents>
        {
          calEvents.map((el, i) => {
            let key = Math.random()
            return (
              <CalendarEvent key={key} className={el.className}>
                {el.title}
              </CalendarEvent>
            )
          })
        }
      </CalendarEvents>
     
     
      {havdala ? <ShabbosDiv>
        {candleLighting ? <HebcalTimedEvent title={`הדלק"נ`} time={formatTime(candleLighting, true)} dotColor="#f8ad7d" /> : null}
        {havdala ?<HebcalTimedEvent title={`סוזש"ק`} time={formatTime(havdala, true)} dotColor="#f8ad7d" /> : null}
        
        {parsh ? <Parsha>{parsh}</Parsha> : null}
      </ShabbosDiv> : null}
    </DayStyles>
  )
}

const Month = ({year, month, createEvent}) => {

  let activeStylists = useLocalStylistsStore(state => state.activeStylists);

  const today = dateStart(Date.now())

  const navigate = useNavigate();

  const queryParams = useQueryParams();

  // const [period, setPeriod] = useState({
  //   ...getStartEndTimes(month, year)
  // })

  let { start, end } = getStartEndTimes(month, year)

  let { scheduledEvents = [], dates = [] } = useDateEvents({start_date: start, end_date: end, range_type: 'month', stylists: activeStylists.filter(el => el.checked).map(el => el.stylist_id)});

  const dayRef = useRef(null)
  const monthRef = useRef(null)

  const [ dayDimensions, setDayDimensions ] = useState({height: 0, width: 0})

  const createNewEvent = (date) => {
    let curDateStr = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
    navigate(`?view=timeline&date=${curDateStr}`);
  }

  useLayoutEffect(() => {
    // let height = dayRef?.current?.clientHeight
    // let width = dayRef?.current?.clientWidth
    // setDayDimensions({height, width})
  }, [])

  // useEffect(() => {
  //   setPeriod({
  //     ...getStartEndTimes(month, year)
  //   })
  // }, [month, year])

  return (
    <Container>
      <Ribbon>
        {dayNames.map(el => <DayName key={el}>{el}</DayName>)}
      </Ribbon>
      <MainCalendar ref={monthRef} className='main-calendar'>
        {dates.map((el, i) => {
          let day = {
            key: Math.random(),
            day: el.getDate(),
            fullDate: el,
            $active: true,
            $today: dateStart(el) === today
          }
          let dayEvents = scheduledEvents.filter(event => findEventInDate(event, el))
            return (
            <Day {...day} key={day.key} scheduledEvents={dayEvents} createEvent={createNewEvent} date={new Date(year, month, el.day)} />
            )
          }
        )}
      </MainCalendar>
    </Container>
  )
}

export default Month;





